import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function VoiceToText() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Voice-to-Text on GNOME: OpenAI Whisper + Speech2Text Extension",
        "July 9, 2026",
        "5min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                I dictate a fair amount of text on this workstation, and typing long-form notes on
                a desktop feels wasteful when a good speech model is sitting right there. This is
                how I turned the GNOME desktop into a dictation machine using OpenAI&apos;s Whisper
                and the Speech2Text extension — fully offline, running locally on CPU.
            </p>

            <h2 className="post-sect">The stack</h2>
            <p>The setup is two pieces that talk to each other over D-Bus:</p>
            <ul className="lead">
                <li>
                    <strong>Speech2Text GNOME extension</strong> — a Shell extension by Kaveh
                    Tehrani (available on GNOME Extensions / SweetTooth). It listens for a keyboard
                    shortcut, records microphone audio, and shows a transcription dialog.
                </li>
                <li>
                    <strong>Whisper service</strong> — a companion D-Bus service running in its own
                    Python virtualenv with OpenAI&apos;s <code>openai-whisper</code> and PyTorch.
                    This is the actual speech-to-text brain.
                </li>
            </ul>
            <p>
                The extension exposes D-Bus methods
                on <code>org.gnome.Shell.Extensions.Speech2Text</code>, including{" "}
                <code>SetWhisperConfig</code> — which is how the model and device are pushed down to
                the service.
            </p>

            <h2 className="post-sect">Installing the companion service</h2>
            <p>
                The extension ships with an official installer that creates the service under{" "}
                <code>~/.local/share/speech2text-extension-service/</code>. The key files there are:
            </p>
            <pre>
                <code className="language-bash">{`~/.local/share/speech2text-extension-service/
├── install-state.conf
├── speech2text-extension-service   # bash wrapper -> venv
└── venv/                           # Python 3.12 virtualenv`}</code>
            </pre>
            <p>The wrapper is a thin script that hands off to the venv:</p>
            <pre>
                <code className="language-bash">{`#!/bin/bash
# Speech2Text Service Wrapper
SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
VENV_DIR="$SCRIPT_DIR/venv"
exec "$VENV_DIR/bin/speech2text-extension-service" "$@"`}</code>
            </pre>

            <h2 className="post-sect">The Whisper model and device</h2>
            <p>
                The installation records its choices in <code>install-state.conf</code>. On this
                machine, that means the <code>small.en</code> model running on CPU:
            </p>
            <pre>
                <code className="language-bash">{`# ~/.local/share/speech2text-extension-service/install-state.conf
device=cpu
model=small.en
installed_at=2026-03-28T11:58:09Z
installer_mode=local`}</code>
            </pre>
            <p>
                The model weights live in Whisper&apos;s cache directory and are downloaded once on
                first use:
            </p>
            <pre>
                <code className="language-bash">{`~/.cache/whisper/small.en.pt   # ~462 MB`}</code>
            </pre>
            <p>The service virtualenv pins the essential pieces for CPU inference:</p>
            <pre>
                <code className="language-bash">{`openai-whisper   20250625
torch            2.11.0+cpu
torchaudio       2.11.0+cpu`}</code>
            </pre>
            <p>
                The service validates the model name it receives over D-Bus against a whitelist —
                you can request names like <code>base</code>, <code>small</code>,{" "}
                <code>small.en</code>, <code>medium</code>, and so on, but an unsupported name is
                rejected before a model is ever loaded. Model and device are separate knobs, so you
                can switch to a GPU backend without touching anything else.
            </p>

            <h2 className="post-sect">Using it: Ctrl+M</h2>
            <p>
                Two settings matter out of the box. On my machine the toggle shortcut and the max
                recording duration are:
            </p>
            <pre>
                <code className="language-bash">{`gsettings list-recursively \\
  org.gnome.shell.extensions.speech2text

# toggle-recording  = ['<Control>m']
# recording-duration = 120`}</code>
            </pre>
            <p>
                Press <strong>Ctrl+M</strong>, speak, and the recording dialog appears. The audio is
                sent to the Whisper service, transcribed, and the result is handed back to the
                extension.
            </p>
            <p>
                Where the text goes next depends on the display server:
            </p>
            <ul className="lead">
                <li>
                    <strong>X11</strong> — the transcribed text can be inserted directly into the
                    active window.
                </li>
                <li>
                    <strong>Wayland</strong> — direct insertion is not available, so the extension
                    copies the transcription to the clipboard (via the Shell&apos;s{" "}
                    <code>St.Clipboard</code>) and shows a review dialog.
                </li>
            </ul>
            <p>
                The copy step is built into the flow: the recording dialog uses the global clipboard
                so the text is ready to paste anywhere, even when auto-insert is unavailable.
            </p>

            <h2 className="post-sect">Under the hood</h2>
            <p>
                The service is an asyncio D-Bus server using <code>dbus-next</code>. It loads the
                Whisper model lazily, validates configuration through <code>SetWhisperConfig</code>,
                and transcribes the recorded WAV in a worker thread so the D-Bus loop stays
                responsive. There is also an idle loop that watches activity and can shut the model
                down to release the RAM it holds when dictation has been idle for a while — useful
                on a CPU-only machine where the <code>small.en</code> model is resident in memory.
            </p>

            <h2 className="post-sect">Tuning for your hardware</h2>
            <p>
                <code>small.en</code> is a good default on CPU: noticeably more accurate than{" "}
                <code>base</code> while staying fast enough to be practical. If you want cleaner
                results and have RAM to spare, move up to <code>medium.en</code>; if you mostly
                dictate short commands and notes, <code>base.en</code> is snappier. And if you own an
                NVIDIA GPU, switching <code>device=gpu</code> in the install state reconfigures the
                service to use CUDA without changing the extension at all.
            </p>

            <h2 className="post-sect">Summary</h2>
            <p>
                Voice-to-text on GNOME is a two-part system: the Speech2Text extension owns the mic,
                the shortcut, and the clipboard, while a local Whisper D-Bus service owns the
                transcription. Everything runs offline on this machine — no API keys, no cloud
                round-trip, no audio leaving the workstation. Press <strong>Ctrl+M</strong> and
                dictate. For notes, for blog drafts, for whatever you would rather say than type.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}